import React, { useLayoutEffect, useMemo, useState } from 'react';
import type { WorkspaceTourStep } from './workspaceTourConfig';
import './WorkspaceTourOverlay.css';

type Props = {
  step: WorkspaceTourStep;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
};

const PAD = 8;
const CARD_GAP = 16;
const CARD_WIDTH = 360;

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

export default function WorkspaceTourOverlay({
  step,
  stepIndex,
  totalSteps,
  onNext,
  onBack,
  onSkip,
}: Props) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const isLast = stepIndex >= totalSteps - 1;
  const isFirst = stepIndex <= 0;

  useLayoutEffect(() => {
    const el = document.querySelector(`[data-tour="${step.target}"]`) as HTMLElement | null;
    if (!el) {
      setRect(null);
      return;
    }
    el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
    const update = () => setRect(el.getBoundingClientRect());
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [step.target, step.id]);

  const cardStyle = useMemo((): React.CSSProperties => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const cardH = 220;
    if (!rect) {
      return {
        left: Math.max(16, (vw - CARD_WIDTH) / 2),
        top: Math.max(16, vh * 0.28),
      };
    }

    let left = rect.right + CARD_GAP;
    let top = rect.top;

    if (step.placement === 'bottom') {
      left = rect.left;
      top = rect.bottom + CARD_GAP;
    } else if (step.placement === 'top') {
      left = rect.left;
      top = rect.top - cardH - CARD_GAP;
    } else if (step.placement === 'left') {
      left = rect.left - CARD_WIDTH - CARD_GAP;
      top = rect.top;
    }

    if (left + CARD_WIDTH > vw - 16) left = vw - CARD_WIDTH - 16;
    if (left < 16) left = 16;
    if (top + cardH > vh - 16) top = vh - cardH - 16;
    if (top < 16) top = 16;

    return { left, top };
  }, [rect, step.placement]);

  const hole = rect
    ? {
        top: clamp(rect.top - PAD, 0, window.innerHeight),
        left: clamp(rect.left - PAD, 0, window.innerWidth),
        width: rect.width + PAD * 2,
        height: rect.height + PAD * 2,
      }
    : null;

  return (
    <div className="dc-tour" role="dialog" aria-modal="true" aria-labelledby="dc-tour-title">
      <div className={`dc-tour__shade${hole ? ' dc-tour__shade--cut' : ''}`} />
      {hole && (
        <div
          className="dc-tour__hole"
          style={{
            top: hole.top,
            left: hole.left,
            width: hole.width,
            height: hole.height,
          }}
        />
      )}
      <div className="dc-tour__card" style={cardStyle}>
        <div className="dc-tour__meta">
          {stepIndex + 1} из {totalSteps}
        </div>
        <h2 id="dc-tour-title" className="dc-tour__title">{step.title}</h2>
        <p className="dc-tour__body">{step.body}</p>
        <div className="dc-tour__actions">
          <button type="button" className="dc-tour__btn dc-tour__btn--ghost" onClick={onSkip}>
            Пропустить
          </button>
          <div className="dc-tour__actions-right">
            <button
              type="button"
              className="dc-tour__btn dc-tour__btn--secondary"
              onClick={onBack}
              disabled={isFirst}
            >
              Назад
            </button>
            <button type="button" className="dc-tour__btn dc-tour__btn--primary" onClick={onNext}>
              {isLast ? 'Готово' : 'Далее'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
